import React from 'react';
import dashboardService from '../../services/dashboardService';

const renderGrowthBadge = rate => {
  if (rate === undefined || rate === null || isNaN(rate)) {
    return (
      <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200'>
        0.0%
      </span>
    );
  }
  const num = Number(rate);
  if (num > 0) {
    return (
      <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60'>
        <svg className='w-3 h-3 text-emerald-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 10l7-7m0 0l7 7m-7-7v18' />
        </svg>
        +{num.toFixed(1)}%
      </span>
    );
  }
  if (num < 0) {
    return (
      <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200/60'>
        <svg className='w-3 h-3 text-rose-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M19 14l-7 7m0 0l-7-7m7 7V3' />
        </svg>
        {num.toFixed(1)}%
      </span>
    );
  }
  return (
    <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200'>
      0.0%
    </span>
  );
};

const DashboardGrowthCards = ({ metrics = {} }) => {
  // Merchant stats
  const newMerchants = metrics.newMerchantsInPeriod || 0;
  const priorMerchants = metrics.priorMerchantsInPeriod || 0;
  const merchantGrowthRate = metrics.merchantGrowthRate;
  const totalMerchants = metrics.totalMerchants || 0;
  const activeMerchants = metrics.activeMerchants || 0;
  const merchantActivationPct = totalMerchants > 0 ? ((activeMerchants / totalMerchants) * 100).toFixed(1) : '0.0';

  // Creator stats
  const newCreators = metrics.newCreatorsInPeriod || 0;
  const priorCreators = metrics.priorCreatorsInPeriod || 0;
  const creatorGrowthRate = metrics.creatorGrowthRate;
  const totalCreators = metrics.totalCreators || 0;
  const activeCreators = metrics.activeCreators || 0;
  const creatorActivePct = totalCreators > 0 ? ((activeCreators / totalCreators) * 100).toFixed(1) : '0.0';

  // Shopper stats
  const newShoppers = metrics.newShoppersInPeriod || 0;
  const priorShoppers = metrics.priorShoppersInPeriod || 0;
  const shopperGrowthRate = metrics.shopperGrowthRate;
  const totalShoppers = metrics.totalShoppers || 0;
  const totalCommunity = metrics.totalCommunity || (totalShoppers + totalCreators);
  const shopperSharePct = totalCommunity > 0 ? ((totalShoppers / totalCommunity) * 100).toFixed(1) : '0.0';

  return (
    <div className='mb-6'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <div className='w-2 h-2 rounded-full bg-emerald-500 animate-pulse' />
          <h2 className='text-xs font-bold uppercase tracking-wider text-gray-500'>
            Ecosystem Growth Hub (Merchants · Creators · Shoppers)
          </h2>
        </div>
        <span className='text-2xs text-gray-400 font-medium'>
          Growth delta compares current period to prior equal window
        </span>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
        {/* Pillar 1: Merchant Growth */}
        <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              <div className='w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors'>
                <svg className='w-4.5 h-4.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' />
                </svg>
              </div>
              <span className='text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Merchants
              </span>
            </div>
            {renderGrowthBadge(merchantGrowthRate)}
          </div>

          <div className='mt-3'>
            <div className='flex items-baseline gap-2'>
              <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
                +{dashboardService.formatNumber(newMerchants)}
              </h3>
              <span className='text-xs text-gray-500 font-medium'>new onboarding</span>
            </div>
            <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
              <span>Prior: <strong className='text-gray-700'>{dashboardService.formatNumber(priorMerchants)}</strong></span>
              <span>Activation: <strong className='text-emerald-600'>{merchantActivationPct}%</strong></span>
            </div>
          </div>
        </div>

        {/* Pillar 2: Creator Growth */}
        <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              <div className='w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors'>
                <svg className='w-4.5 h-4.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z' />
                </svg>
              </div>
              <span className='text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Creators
              </span>
            </div>
            {renderGrowthBadge(creatorGrowthRate)}
          </div>

          <div className='mt-3'>
            <div className='flex items-baseline gap-2'>
              <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
                +{dashboardService.formatNumber(newCreators)}
              </h3>
              <span className='text-xs text-gray-500 font-medium'>new affiliates</span>
            </div>
            <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
              <span>Prior: <strong className='text-gray-700'>{dashboardService.formatNumber(priorCreators)}</strong></span>
              <span>Active: <strong className='text-purple-600'>{creatorActivePct}%</strong></span>
            </div>
          </div>
        </div>

        {/* Pillar 3: Shopper Growth */}
        <div className='relative overflow-hidden bg-white p-5 rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-shadow group'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              <div className='w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors'>
                <svg className='w-4.5 h-4.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' />
                </svg>
              </div>
              <span className='text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                Shoppers
              </span>
            </div>
            {renderGrowthBadge(shopperGrowthRate)}
          </div>

          <div className='mt-3'>
            <div className='flex items-baseline gap-2'>
              <h3 className='text-2xl font-bold text-gray-900 tracking-tight'>
                +{dashboardService.formatNumber(newShoppers)}
              </h3>
              <span className='text-xs text-gray-500 font-medium'>new members</span>
            </div>
            <div className='flex items-center justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100'>
              <span>Prior: <strong className='text-gray-700'>{dashboardService.formatNumber(priorShoppers)}</strong></span>
              <span>Share: <strong className='text-indigo-600'>{shopperSharePct}%</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardGrowthCards;
